import {
  Box,
  FormControl,
  Input,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  numberToString,
  stringToNumber,
  getDisplayDecimalPlaces,
} from '../../../special/numberConverter';
import BallotIcon from '@mui/icons-material/Ballot';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { round } from '../../../special/round';
import { tokens } from '../../../../global/theme/tokens';

const EntryEditable = ({ col, item, onChange, onFocus = () => {} }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const textFieldRef = useRef(null);
  const hasInteracted = useRef(false);
  const { t } = useTranslation();
  const [value, setValue] = useState(null);
  const [shownValue, setShownValue] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [debouncedValue] = useDebounce(value, 400);

  const numToStringDecimalPlaces = 16;

  const usePercent =
    (item.type == 'hoaiPhase' ||
      item.type == 'percent' ||
      item.type == 'percentEditable') &&
    col.key == 'amount';

  useEffect(() => {
    if (debouncedValue && hasInteracted.current)
      onChange({ target: { value: debouncedValue } });
  }, [debouncedValue]);

  useEffect(() => {
    if (!isFocused || item[col.key] !== stringToNumber(value)) {
      if (usePercent) {
        setShownValue(
          numberToString(item[col.key] * 100, col.decimalPlaces ?? 2),
        );
        setValue(numberToString(item[col.key], numToStringDecimalPlaces));
      } else
        setValue(
          col.number
            ? numberToString(
                item[col.key],
                getDisplayDecimalPlaces(item[col.key], 2, col.decimalPlaces),
              )
            : item[col.key],
        );
    }
  }, [item[col.key]]);

  function handleFocus() {
    if (
      usePercent &&
      stringToNumber(value, numToStringDecimalPlaces) * 100 == 0
    ) {
      setValue('');
      setShownValue('');
    } else if (parseFloat(value) == 0) {
      setValue('');
    }
    onFocus(textFieldRef);
    setIsFocused(true);
  }
  function handleBlur(e) {
    if (usePercent) {
      /*onChange({
        target: {
          value: numberToString(
            stringToNumber(e.target.value, numToStringDecimalPlaces) / 100,
            numToStringDecimalPlaces
          ),
        },
      });*/
      setValue(
        numberToString(
          stringToNumber(e.target.value, numToStringDecimalPlaces) / 100,
          numToStringDecimalPlaces,
        ),
      );
      setShownValue(
        numberToString(
          stringToNumber(e.target.value, numToStringDecimalPlaces),
          col.decimalPlaces,
        ),
      );
    } else {
      const parsed = stringToNumber(e.target.value, numToStringDecimalPlaces);
      setValue(
        numberToString(
          parsed,
          getDisplayDecimalPlaces(parsed, 2, col.decimalPlaces),
        ),
      );
    }
    setIsFocused(false);
  }

  function handleChange(e) {
    hasInteracted.current = true;
    if (usePercent) {
      setValue(
        numberToString(
          stringToNumber(e.target.value, numToStringDecimalPlaces) / 100,
        ),
        numToStringDecimalPlaces,
      );
      setShownValue(e.target.value);
    } else setValue(e.target.value);
  }

  let openAmount = null;
  if (
    col.key === 'name' &&
    item.billedAmount != null &&
    item.totalAmount != null
  ) {
    // Converted positions have mixed units: totalAmount/billedAmount are
    // fractions (from order) but amount/price are flat (from conversion).
    // Only applies to positions that were converted away from their original
    // percentage-based type (hoaiPhase/percent → default).
    const isConverted =
      item.orderedPrice &&
      item.orderedPrice !== item.price &&
      item.type !== 'hoaiPhase' &&
      item.type !== 'percent' &&
      item.type !== 'percentEditable';

    if (isConverted) {
      const currentAmountInOriginalUnits =
        item.orderedPrice != 0
          ? (item.amount * item.price) / item.orderedPrice
          : 0;
      openAmount = round(
        item.totalAmount - item.billedAmount - currentAmountInOriginalUnits,
        4,
      );
    } else if (
      item.type == 'hoaiPhase' ||
      item.type == 'percent' ||
      item.type == 'percentEditable'
    ) {
      openAmount = round(
        (item.totalAmount - item.billedAmount - item.amount) * 100,
        4,
      );
    } else {
      openAmount = round(item.totalAmount - item.billedAmount - item.amount, 4);
    }
  }

  return (
    <Box className="flex">
      <Box
        className="flex flex-col w-full"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Box className="w-full flex items-center gap-2">
          {item && item.isOrderPosition && col.key === 'name' && (
            <Tooltip title={t('Position from order')}>
              <BallotIcon className="mr-2" />
            </Tooltip>
          )}
          <FormControl fullWidth sx={{ m: 0 }}>
            <Input
              disableInjectingGlobalStyles={true}
              ref={textFieldRef}
              onFocus={handleFocus}
              onBlur={col.number ? handleBlur : () => {}}
              size="small"
              className={col.className}
              id={'pos-input-' + col.key}
              value={(usePercent ? shownValue : (value ?? '')) ?? ''}
              onChange={handleChange}
              endAdornment={
                <InputAdornment position="end">
                  {usePercent ? '%' : col.unit}
                </InputAdornment>
              }
            />
          </FormControl>
        </Box>
      </Box>
      {col.key === 'name' &&
        Boolean(item.billedAmount != null) &&
        Boolean(item.totalAmount != null) &&
        Boolean(openAmount || openAmount == 0) && (
          <Box className="p-1">
            <Tooltip title={t('Open quantity')}>
              <Typography
                fontWeight={600}
                color={
                  openAmount == 0
                    ? colors.grey[600]
                    : openAmount < 0
                      ? colors.error
                      : colors.success
                }
              >
                {item.type == 'hoaiPhase' ||
                item.type == 'percent' ||
                item.type == 'percentEditable'
                  ? round(openAmount) + '%'
                  : round(openAmount)}
              </Typography>
            </Tooltip>
          </Box>
        )}
    </Box>
  );
};

export default EntryEditable;
